import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BdcDashboardStoreCell, BdcDashboardStoreRow } from "../../../../types/bdcDashboard.type";
import { pcs } from "../../format";
import Tag from "../../Tag";
import { CHART_COLORS } from "../constants";
import {
  avgText,
  cadenceText,
  cellTitle,
  cellTone,
  CellTone,
  dayMonth,
  levelTag,
  mondayFirstCells,
  WEEKDAY_SHORT_MON_FIRST,
} from "../format";

const CELL_STYLE: Record<CellTone, React.CSSProperties> = {
  ordered: { background: CHART_COLORS.cellOrdered },
  missed: { background: CHART_COLORS.cellMissed },
  lapsed: { background: CHART_COLORS.cellLapsed },
  nofile: { background: CHART_COLORS.cellNoFile, boxShadow: `inset 0 0 0 1px ${CHART_COLORS.axis}` },
};

const CAL_SX = { display: "grid", gridTemplateColumns: "repeat(7, 12px)", gap: "2px" } as const;
const SUB_SX = { display: "block", fontSize: "0.75rem", color: "text.secondary" } as const;

function Cell({ cell, lastOrderDate }: Readonly<{ cell: BdcDashboardStoreCell; lastOrderDate: string }>) {
  const tone = cellTone(cell, lastOrderDate);
  return (
    <span
      title={cellTitle(cell, lastOrderDate)}
      data-tone={tone}
      style={{ display: "inline-block", width: 12, height: 12, borderRadius: 2, ...CELL_STYLE[tone] }}
    />
  );
}

/** Four weeks, one row per Mon–Sun week, so fixed order days read as columns. */
function OrderCalendar({ row }: Readonly<{ row: BdcDashboardStoreRow }>) {
  return (
    <Box sx={CAL_SX}>
      {mondayFirstCells(row.cells).map((cell, index) =>
        cell ? (
          <Cell key={cell.date} cell={cell} lastOrderDate={row.last_order_date} />
        ) : (
          <span key={`pad-${index}`} aria-hidden="true" />
        ),
      )}
    </Box>
  );
}

export function CellLegend() {
  const items: { tone: CellTone; label: string }[] = [
    { tone: "ordered", label: "สั่ง" },
    { tone: "missed", label: "ไม่สั่ง (ตามรอบ)" },
    { tone: "lapsed", label: "ไม่สั่ง หลังครั้งล่าสุด" },
    { tone: "nofile", label: "ไม่มีไฟล์ (ไม่นับ)" },
  ];
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
      {items.map((item) => (
        <Box key={item.tone} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.625 }}>
          <span
            style={{ display: "inline-block", width: 12, height: 12, borderRadius: 2, ...CELL_STYLE[item.tone] }}
          />
          <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Stores behind their own habit, highest lost volume first (the API's order). Sits inside TABLE_SX. */
export default function StoreTable({
  rows,
  branchOf,
}: Readonly<{ rows: BdcDashboardStoreRow[]; branchOf?: (companyId: string) => string }>) {
  if (rows.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography className="sub">ไม่มีร้านที่หลุดรอบตามเงื่อนไขนี้</Typography>
      </Box>
    );
  }
  const cells = rows[0].cells;
  const calendarHead = cells.length ? `${dayMonth(cells[0].date)} → ${dayMonth(cells[cells.length - 1].date)}` : "";
  return (
    <Box sx={{ overflowX: "auto" }}>
      <table className="bdc-t">
        <thead>
          <tr>
            <th>ร้าน</th>
            {branchOf && <th>สาขา</th>}
            <th>DC / สาย</th>
            <th>รอบปกติ</th>
            <th>สั่งล่าสุด</th>
            <th>สถานะ</th>
            <th className="n">ยอดหาย (ชิ้น)</th>
            <th>
              {calendarHead}
              <Box sx={{ ...CAL_SX, fontSize: "0.625rem", fontWeight: 400, textAlign: "center", mt: 0.25 }}>
                {WEEKDAY_SHORT_MON_FIRST.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </Box>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const tag = levelTag(row);
            return (
              <tr key={`${row.company_id}/${row.store_code}`}>
                <td>
                  <span className="mono">{row.store_code}</span> {row.store_name}
                </td>
                {branchOf && <td>{branchOf(row.company_id)}</td>}
                <td>
                  {row.dc_code} / {row.line_no}
                </td>
                <td>
                  {cadenceText(row)}
                  <Typography component="span" sx={SUB_SX}>
                    สั่ง {pcs(row.order_days)}/{pcs(row.window_days)} วัน
                  </Typography>
                </td>
                <td>
                  {row.last_order_date ? dayMonth(row.last_order_date) : "—"}
                  <Typography component="span" sx={SUB_SX}>
                    เงียบ {pcs(row.streak)} วัน
                  </Typography>
                </td>
                <td>
                  <Tag tone={tag.tone}>{tag.text}</Tag>
                  <Typography component="span" sx={SUB_SX}>
                    ควรสั่งแล้ว ~{avgText(row.expected_missed)} ครั้ง
                  </Typography>
                </td>
                <td className="n">
                  {pcs(row.lost_volume)}
                  <Typography component="span" sx={SUB_SX}>
                    {avgText(row.avg_qty_per_order)} ชิ้น/ครั้ง
                  </Typography>
                </td>
                <td>
                  <OrderCalendar row={row} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}
